/**
 * Câmeras e placas de captura ligadas ao computador.
 *
 * O navegador não distingue uma coisa da outra: uma placa de captura entra como
 * mais um `videoinput`, exatamente como uma webcam. Quem sabe a diferença é
 * quem ligou o cabo, então a escolha fica com a pessoa e a plataforma só oferece
 * a lista.
 */

export interface DispositivoDeVideo {
  deviceId: string
  label: string
  /**
   * Entrada de áudio do mesmo aparelho físico, quando existe.
   *
   * Uma placa de captura entrega imagem e som, mas o navegador os apresenta
   * como dois dispositivos separados, sem nada no nome que os ligue de forma
   * confiável. O que os une é o `groupId`, que o navegador atribui por hardware:
   * é por ele que se descobre qual entrada de áudio traz o som daquele vídeo.
   */
  audioDeviceId?: string
}

/**
 * Os rótulos só existem depois de uma permissão concedida.
 *
 * Sem ela o navegador devolve nome vazio, e uma lista de "Dispositivo 1, 2, 3"
 * não ajuda ninguém a achar a placa de captura no meio das webcams.
 */
export interface EntradaDeSom {
  deviceId: string
  label: string
}

export async function listarDispositivosDeVideo(): Promise<{
  dispositivos: DispositivoDeVideo[]
  /** Todas as entradas de som, para quem precisar escolher na mão. */
  entradasDeSom: EntradaDeSom[]
  temRotulos: boolean
}> {
  try {
    const todos = await navigator.mediaDevices.enumerateDevices()
    const entradasDeVideo = todos.filter((d) => d.kind === 'videoinput')
    const entradasDeAudio = todos.filter((d) => d.kind === 'audioinput')

    return {
      dispositivos: entradasDeVideo.map((d, indice) => {
        /*
          O microfone padrão do sistema costuma aparecer com groupId próprio, e
          o "default" duplicado do Chrome também. Casar por grupo evita pegar o
          microfone da mesa achando que é o som da placa.
        */
        const audioIrmao = d.groupId
          ? entradasDeAudio.find((a) => a.groupId === d.groupId && a.deviceId !== 'default')
          : undefined

        return {
          deviceId: d.deviceId,
          label: d.label || `Dispositivo de vídeo ${indice + 1}`,
          audioDeviceId: audioIrmao?.deviceId,
        }
      }),
      entradasDeSom: entradasDeAudio
        .filter((a) => a.deviceId !== 'default' && a.deviceId !== 'communications')
        .map((a, indice) => ({
          deviceId: a.deviceId,
          label: a.label || `Entrada de som ${indice + 1}`,
        })),
      temRotulos: entradasDeVideo.some((d) => d.label.length > 0),
    }
  } catch {
    return { dispositivos: [], entradasDeSom: [], temRotulos: false }
  }
}

/**
 * Pede acesso só para destravar os nomes dos dispositivos.
 *
 * Pede áudio junto porque a permissão é por tipo: sem ela, as entradas de som
 * continuam sem nome e sem `groupId` utilizável, e o vínculo entre a imagem e o
 * som da placa de captura não pode ser feito.
 */
export async function pedirPermissaoDeVideo(): Promise<boolean> {
  try {
    const fluxo = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
    fluxo.getTracks().forEach((t) => t.stop())
    return true
  } catch {
    return false
  }
}
